import { BubbleMenuItem } from '.';
import { Editor } from '@tiptap/core';
import { Popover } from 'radix-ui';
import { Dispatch, FC, SetStateAction } from 'react';

import { Icon, Stack } from '@eluve/components';

interface NodeSelectorProps {
  editor: Editor;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

export const NodeSelector: FC<NodeSelectorProps> = ({
  editor,
  isOpen,
  setIsOpen,
}) => {
  const items: BubbleMenuItem[] = [
    {
      name: 'Text',
      icon: 'Text',
      command: () =>
        editor.chain().focus().toggleNode('paragraph', 'paragraph').run(),
      // I feel like there has to be a more efficient way to do this – feel free to PR if you know how!
      isActive: () =>
        editor.isActive('paragraph') &&
        !editor.isActive('bulletList') &&
        !editor.isActive('orderedList'),
    },
    {
      name: 'Heading 1',
      icon: 'Heading1',
      command: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
      isActive: () => editor.isActive('heading', { level: 1 }),
    },
    {
      name: 'Heading 2',
      icon: 'Heading2',
      command: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
      isActive: () => editor.isActive('heading', { level: 2 }),
    },
    {
      name: 'Heading 3',
      icon: 'Heading3',
      command: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
      isActive: () => editor.isActive('heading', { level: 3 }),
    },
    {
      name: 'Bullet List',
      icon: 'ListOrdered',
      command: () => editor.chain().focus().toggleBulletList().run(),
      isActive: () => editor.isActive('bulletList'),
    },
    {
      name: 'Numbered List',
      icon: 'ListOrdered',
      command: () => editor.chain().focus().toggleOrderedList().run(),
      isActive: () => editor.isActive('orderedList'),
    },
  ];

  const activeItem = items.filter((item) => item.isActive()).pop() ?? {
    name: 'Multiple',
  };

  return (
    <Popover.Root open={isOpen}>
      <div className="relative">
        <Popover.Trigger
          className="flex h-full items-center gap-1 p-2 text-sm font-medium whitespace-nowrap text-text-tertiary hover:bg-bg-tertiary active:bg-border-primary"
          onClick={() => setIsOpen(!isOpen)}
        >
          <span>{activeItem?.name}</span>
          <Icon name="ChevronDown" />
        </Popover.Trigger>

        <Popover.Content
          align="start"
          className="z-[99999] my-1 flex max-h-80 w-48 animate-in flex-col overflow-hidden overflow-y-auto rounded-sm border border-border-primary bg-white p-1 shadow-xl fade-in slide-in-from-top-1"
        >
          {items.map((item, index) => (
            <button
              key={index}
              onClick={() => {
                item.command();
                setIsOpen(false);
              }}
              className="flex items-center justify-between rounded-sm px-2 py-1 hover:bg-bg-tertiary"
              type="button"
            >
              <Stack
                className="space-x-2"
                direction="horizontal"
                align="center"
                fullWidth={false}
              >
                <div className="rounded-xs border border-border-primary p-1">
                  {item.icon && <Icon name={item.icon} size="xs" />}
                </div>
                <span className="text-sm font-medium text-text-tertiary">
                  {item.name}
                </span>
              </Stack>
              {activeItem.name === item.name && <Icon name="Check" />}
            </button>
          ))}
        </Popover.Content>
      </div>
    </Popover.Root>
  );
};
